import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { firestore } from "../../firebase";
import { collection, query, orderBy, getDocs } from "firebase/firestore";
import ReactPaginate from "react-paginate";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

/* ---------- Hero / Slider (startsida) ---------- */

const HeroSection = styled.section`
  margin-top: 1.5rem;
  margin-bottom: 1.2rem;
`;

const SliderCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  overflow: hidden;
`;

const Slide = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  height: 280px;

  @media (max-width: 768px) {
    height: 360px;
  }
`;

const SlideImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1.02);
  display: block;
  background: #000;
`;

const SlideOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.12) 0%,
    rgba(0, 0, 0, 0.78) 85%
  );
`;

const SwipeHint = styled.div`
  position: absolute;
  top: 14px;
  left: 14px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-weight: 900;
  font-size: 12px;
  letter-spacing: 0.2px;
  display: none;

  @media (max-width: 768px) {
    display: inline-flex;
  }
`;

const SlideContent = styled.div`
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 14px;

  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(229, 231, 235, 0.9);
  border-radius: 16px;
  box-shadow: 0 16px 44px rgba(0, 0, 0, 0.18);
  padding: 14px;

  @media (min-width: 1024px) {
    max-width: 520px;
  }
`;

const SlideTitle = styled.h3`
  margin: 0;
  font-weight: 950;
  color: #111827;
  font-size: 16px;
`;

const SlideText = styled.p`
  margin: 8px 0 0;
  color: #4b5563;
  line-height: 1.55;
  font-size: 14px;
`;

const SlideActions = styled.div`
  margin-top: 10px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const CTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid rgba(15, 118, 110, 0.25);
  background: rgba(15, 118, 110, 0.1);
  color: #0f766e;
  font-weight: 950;
  text-decoration: none;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.04);
  }
`;

/* ---------- Grid (startsida) ---------- */

const GridCard = styled.section`
  margin-top: 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  padding: 18px;
`;

const GridTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 950;
  color: #111827;
`;

const NewsGrid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
`;

const NewsCard = styled.article`
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.06);
  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 44px rgba(0, 0, 0, 0.1);
  }
`;

const CardImg = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
  display: block;
  background: #000;
`;

const CardBody = styled.div`
  padding: 12px 14px 14px;
`;

const CardTitle = styled.h4`
  margin: 0;
  font-size: 15px;
  font-weight: 950;
  color: #111827;
  line-height: 1.35;
`;

const CardLink = styled(Link)`
  display: inline-flex;
  margin-top: 10px;
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-weight: 950;
  text-decoration: none;

  &:hover {
    background: #f9fafb;
    transform: translateY(-1px);
  }
`;

/* ---------- Pagination (EXAKT samma stil som News-sidan) ---------- */

const PaginateWrap = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: center;

  .pagination {
    display: flex;
    gap: 8px;
    list-style: none;
    padding: 0;
    margin: 0;
    flex-wrap: wrap;
    justify-content: center;
  }

  .page-item {
    border: 1px solid #e5e7eb;
    background: #fff;
    border-radius: 999px;
    padding: 10px 14px;
    font-weight: 950;
    color: #111827;
    cursor: pointer;
    transition: transform 140ms ease, background 140ms ease;
  }

  .page-item:hover {
    background: #f9fafb;
    transform: translateY(-1px);
  }

  .active {
    background: rgba(15, 118, 110, 0.12);
    border-color: rgba(15, 118, 110, 0.2);
    color: #0f766e;
  }
`;

const Empty = styled.div`
  padding: 8px 0;
  color: #6b7280;
  font-weight: 800;
`;

/* ---------- Component ---------- */

export default function NewsSection() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(0);
  const newsPerPage = 4;

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const newsCollection = collection(firestore, "news");
        const q = query(newsCollection, orderBy("createdAt", "desc"));
        const snap = await getDocs(q);
        setNews(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  const latestNews = useMemo(() => news.slice(0, 3), [news]);
  const remainingNews = useMemo(() => news.slice(3), [news]);

  // om nyheter ändras: se till att sidan inte hamnar "utanför"
  useEffect(() => {
    setCurrentPage(0);
  }, [news.length]);

  const offset = currentPage * newsPerPage;
  const currentNews = useMemo(
    () => remainingNews.slice(offset, offset + newsPerPage),
    [remainingNews, offset]
  );

  const handlePageClick = (event) => setCurrentPage(event.selected);

  return (
    <HeroSection>
      {/* Top slider */}
      <SliderCard>
        <Swiper
          modules={[Autoplay, Pagination]}
          spaceBetween={14}
          slidesPerView={1}
          loop
          autoplay={{ delay: 5500, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          breakpoints={{
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          }}
        >
          {latestNews.map((item) => (
            <SwiperSlide key={item.id}>
              <Slide>
                <SlideImg src={item.image1} alt={item.title} />
                <SlideOverlay />
                <SwipeHint>Swipa för mer</SwipeHint>

                <SlideContent>
                  <SlideTitle>{item.title}</SlideTitle>
                  <SlideText>
                    {item.excerpt || "Läs de senaste nyheterna från vår klubb."}
                  </SlideText>
                  <SlideActions>
                    <CTA to={`/news/${item.id}`}>Läs mer</CTA>
                  </SlideActions>
                </SlideContent>
              </Slide>
            </SwiperSlide>
          ))}
        </Swiper>
      </SliderCard>

      {/* Grid + pagination */}
      <GridCard>
        <GridTitle>Fler nyheter</GridTitle>

        {loading ? (
          <Empty>Laddar nyheter…</Empty>
        ) : !currentNews.length ? (
          <Empty>Inga fler nyheter just nu.</Empty>
        ) : (
          <NewsGrid>
            {currentNews.map((item) => (
              <NewsCard key={item.id}>
                {item.image1 && <CardImg src={item.image1} alt={item.title} />}
                <CardBody>
                  <CardTitle>{item.title}</CardTitle>
                  <CardLink to={`/news/${item.id}`}>Läs hela nyheten</CardLink>
                </CardBody>
              </NewsCard>
            ))}
          </NewsGrid>
        )}

        {remainingNews.length > newsPerPage && (
          <PaginateWrap>
            <ReactPaginate
              previousLabel={"←"}
              nextLabel={"→"}
              breakLabel={"…"}
              pageCount={Math.ceil(remainingNews.length / newsPerPage)}
              marginPagesDisplayed={1}
              pageRangeDisplayed={3}
              onPageChange={handlePageClick}
              containerClassName={"pagination"}
              activeClassName={"active"}
              previousClassName={"page-item"}
              nextClassName={"page-item"}
              pageClassName={"page-item"}
              breakClassName={"page-item"}
            />
          </PaginateWrap>
        )}
      </GridCard>
    </HeroSection>
  );
}

